import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Typography,
  Descriptions,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  message,
  Select,
  Switch,
  Divider,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import api from "../../api";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

interface UltraLoungeEvent {
  id: number | string;
  name: string;
  internal_code: string | null;
  description: string | null;

  event_type: string | null;
  status: string | null;
  is_all_day: boolean;

  start_datetime: string | null;
  end_datetime: string | null;

  estimated_cost: number | null;
  estimated_revenue: number | null;
  actual_cost: number | null;
  actual_revenue: number | null;

  ticket_revenue: number | null;
  fnb_revenue: number | null;
  marketing_cost: number | null;
  entertainment_cost: number | null;

  misc_cost: number | null;
  misc_cost_notes: string;
  misc_revenue: number | null;
  misc_revenue_notes: string;

  primary_sponsor: string | null;
  notes: string | null;

  pnl?: number | null;
}

// Mirror backend choices
const EVENT_TYPE_OPTIONS = [
  { value: "concert", label: "Concert / Live" },
  { value: "club_night", label: "Club Night / DJ" },
  { value: "private", label: "Private / Buyout" },
  { value: "brand", label: "Brand Activation" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "planned", label: "Planned" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

type EventFormValues = {
  name: string;
  internal_code?: string;
  description?: string;
  event_type?: string;
  status?: string;
  is_all_day?: boolean;
  dates?: [Dayjs, Dayjs];
  estimated_cost?: number;
  estimated_revenue?: number;
  actual_cost?: number;
  actual_revenue?: number;
  ticket_revenue?: number;
  fnb_revenue?: number;
  marketing_cost?: number;
  entertainment_cost?: number;
  misc_cost?: number;
  misc_cost_notes?: string;
  misc_revenue?: number;
  misc_revenue_notes?: string;
  primary_sponsor?: string;
  notes?: string;
};

const EventDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<UltraLoungeEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [form] = Form.useForm<EventFormValues>();

  const loadEvent = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/ul/events/${id}/`);
      const data = res.data;

      const parsed: UltraLoungeEvent = {
        id: data.id,
        name: data.name,
        internal_code: data.internal_code ?? null,
        description: data.description ?? null,

        event_type: data.event_type ?? null,
        status: data.status ?? null,
        is_all_day: Boolean(data.is_all_day),

        start_datetime: data.start_datetime,
        end_datetime: data.end_datetime,

        estimated_cost:
          data.estimated_cost != null ? Number(data.estimated_cost) : null,
        estimated_revenue:
          data.estimated_revenue != null ? Number(data.estimated_revenue) : null,
        actual_cost:
          data.actual_cost != null ? Number(data.actual_cost) : null,
        actual_revenue:
          data.actual_revenue != null ? Number(data.actual_revenue) : null,

        ticket_revenue:
          data.ticket_revenue != null ? Number(data.ticket_revenue) : null,
        fnb_revenue:
          data.fnb_revenue != null ? Number(data.fnb_revenue) : null,
        marketing_cost:
          data.marketing_cost != null ? Number(data.marketing_cost) : null,
        entertainment_cost:
          data.entertainment_cost != null
            ? Number(data.entertainment_cost)
            : null,

        misc_cost: data.misc_cost != null ? Number(data.misc_cost) : null,
        misc_cost_notes: data.misc_cost_notes || "",
        misc_revenue:
          data.misc_revenue != null ? Number(data.misc_revenue) : null,
        misc_revenue_notes: data.misc_revenue_notes || "",

        primary_sponsor: data.primary_sponsor ?? null,
        notes: data.notes ?? null,

        pnl: data.pnl != null ? Number(data.pnl) : null,
      };

      setEvent(parsed);
    } catch (err) {
      console.error(err);
      message.error("Failed to load event details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEditModal = () => {
    if (!event) return;

    const rangeValue: [Dayjs, Dayjs] | undefined =
      event.start_datetime && event.end_datetime
        ? [dayjs(event.start_datetime), dayjs(event.end_datetime)]
        : undefined;

    form.setFieldsValue({
      name: event.name,
      internal_code: event.internal_code || undefined,
      description: event.description || undefined,
      event_type: event.event_type || "other",
      status: event.status || "planned",
      is_all_day: event.is_all_day,
      dates: rangeValue,

      estimated_cost: event.estimated_cost ?? undefined,
      estimated_revenue: event.estimated_revenue ?? undefined,
      actual_cost: event.actual_cost ?? undefined,
      actual_revenue: event.actual_revenue ?? undefined,

      ticket_revenue: event.ticket_revenue ?? undefined,
      fnb_revenue: event.fnb_revenue ?? undefined,
      marketing_cost: event.marketing_cost ?? undefined,
      entertainment_cost: event.entertainment_cost ?? undefined,

      misc_cost: event.misc_cost ?? undefined,
      misc_cost_notes: event.misc_cost_notes || "",
      misc_revenue: event.misc_revenue ?? undefined,
      misc_revenue_notes: event.misc_revenue_notes || "",

      primary_sponsor: event.primary_sponsor || undefined,
      notes: event.notes || undefined,
    });

    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    form.resetFields();
  };

  const handleEditSubmit = async (values: EventFormValues) => {
    if (!id) return;

    const [start, end] = values.dates || [];

    const payload: any = {
      name: values.name,
      internal_code: values.internal_code ?? null,
      description: values.description ?? null,
      event_type: values.event_type ?? "other",
      status: values.status ?? "planned",
      is_all_day: values.is_all_day ?? false,

      estimated_cost: values.estimated_cost ?? null,
      estimated_revenue: values.estimated_revenue ?? null,
      actual_cost: values.actual_cost ?? null,
      actual_revenue: values.actual_revenue ?? null,

      ticket_revenue: values.ticket_revenue ?? null,
      fnb_revenue: values.fnb_revenue ?? null,
      marketing_cost: values.marketing_cost ?? null,
      entertainment_cost: values.entertainment_cost ?? null,

      misc_cost: values.misc_cost ?? null,
      misc_cost_notes: values.misc_cost_notes ?? "",
      misc_revenue: values.misc_revenue ?? null,
      misc_revenue_notes: values.misc_revenue_notes ?? "",

      primary_sponsor: values.primary_sponsor ?? null,
      notes: values.notes ?? null,
    };

    if (start && end) {
      payload.start_datetime = start.toISOString();
      payload.end_datetime = end.toISOString();
    }

    try {
      await api.patch(`/ul/events/${id}/`, payload);
      message.success("Event updated.");
      await loadEvent();
      closeEditModal();
    } catch (err: any) {
      console.error(err);
      if (err.response?.data) {
        const data = err.response.data;
        const messages = Object.values(data).flat();
        message.error(String(messages[0] ?? "Failed to update event."));
      } else {
        message.error("Failed to update event.");
      }
    }
  };

  if (!event && !loading) {
    return (
      <div style={{ padding: "40px" }}>
        <Text type="secondary">Event not found.</Text>
      </div>
    );
  }

  const formatCurrency = (value: number | null | undefined) =>
    value != null ? value.toLocaleString() : "—";

  const formatDateRange = (e: UltraLoungeEvent | null) => {
    if (!e || !e.start_datetime || !e.end_datetime) return "TBD";
    return `${e.start_datetime.slice(0, 10)} ~ ${e.end_datetime.slice(0, 10)}`;
  };

  const findLabel = (options: { value: string; label: string }[], value?: string | null) =>
    options.find((o) => o.value === value)?.label || "—";

  return (
    <div style={{ padding: "40px", background: "#f0f2f5", minHeight: "100vh" }}>
      {/* Header */}
      <Card
        bordered={false}
        style={{
          maxWidth: 900,
          margin: "0 auto 24px auto",
          borderRadius: 16,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
        loading={loading}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
          }}
        >
          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              Ultra Lounge Event Details
            </Title>
            <Text type="secondary">
              Detailed breakdown of costs, revenue, and P&amp;L for this event.
            </Text>
          </div>

          <Button type="primary" onClick={openEditModal}>
            Edit Event
          </Button>
        </div>
      </Card>

      {/* Details */}
      <Card
        bordered={false}
        style={{
          maxWidth: 900,
          margin: "0 auto",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
        loading={loading}
      >
        {event && (
          <>
            {/* Basic Info */}
            <Descriptions title="Basic Info" bordered column={1} size="small">
              <Descriptions.Item label="Event ID">{event.id}</Descriptions.Item>
              <Descriptions.Item label="Event Name">{event.name}</Descriptions.Item>
              <Descriptions.Item label="Internal Code">
                {event.internal_code || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Event Type">
                {findLabel(EVENT_TYPE_OPTIONS, event.event_type)}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {findLabel(STATUS_OPTIONS, event.status)}
              </Descriptions.Item>
              <Descriptions.Item label="All Day?">
                {event.is_all_day ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {event.description || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Primary Sponsor">
                {event.primary_sponsor || "—"}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* Schedule */}
            <Descriptions title="Schedule" bordered column={1} size="small">
              <Descriptions.Item label="Event Dates">
                {formatDateRange(event)}
              </Descriptions.Item>
              <Descriptions.Item label="Start Datetime">
                {event.start_datetime || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="End Datetime">
                {event.end_datetime || "—"}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* Financials */}
            <Descriptions title="Financial Summary" bordered column={1} size="small">
              <Descriptions.Item label="Estimated Cost (₩)">
                {formatCurrency(event.estimated_cost)}
              </Descriptions.Item>
              <Descriptions.Item label="Estimated Revenue (₩)">
                {formatCurrency(event.estimated_revenue)}
              </Descriptions.Item>
              <Descriptions.Item label="Actual Cost (₩)">
                {formatCurrency(event.actual_cost)}
              </Descriptions.Item>
              <Descriptions.Item label="Actual Revenue (₩)">
                {formatCurrency(event.actual_revenue)}
              </Descriptions.Item>
              <Descriptions.Item label="Net P&L (₩)">
                {event.pnl != null ? event.pnl.toLocaleString() : "—"}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* Breakdown */}
            <Descriptions title="Breakdown" bordered column={1} size="small">
              <Descriptions.Item label="Ticket / Door Revenue (₩)">
                {formatCurrency(event.ticket_revenue)}
              </Descriptions.Item>
              <Descriptions.Item label="F&B Revenue (₩)">
                {formatCurrency(event.fnb_revenue)}
              </Descriptions.Item>
              <Descriptions.Item label="Marketing Cost (₩)">
                {formatCurrency(event.marketing_cost)}
              </Descriptions.Item>
              <Descriptions.Item label="Entertainment Cost (₩)">
                {formatCurrency(event.entertainment_cost)}
              </Descriptions.Item>
              <Descriptions.Item label="Misc Cost (₩)">
                {formatCurrency(event.misc_cost)}
              </Descriptions.Item>
              <Descriptions.Item label="Misc Cost Notes">
                {event.misc_cost_notes || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Misc Revenue (₩)">
                {formatCurrency(event.misc_revenue)}
              </Descriptions.Item>
              <Descriptions.Item label="Misc Revenue Notes">
                {event.misc_revenue_notes || "—"}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* Notes */}
            <Descriptions title="Notes" bordered column={1} size="small">
              <Descriptions.Item label="Internal Notes">
                {event.notes || "—"}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Card>

      {/* Edit Event Modal */}
      <Modal
        title="Edit Event"
        open={editModalOpen}
        onCancel={closeEditModal}
        onOk={() => form.submit()}
        okText="Save"
        destroyOnClose
      >
        <Form<EventFormValues> form={form} layout="vertical" onFinish={handleEditSubmit}>
          <Form.Item
            label="Event Name"
            name="name"
            rules={[{ required: true, message: "Please enter the event name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Internal Code" name="internal_code">
            <Input />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item label="Event Type" name="event_type">
            <Select options={EVENT_TYPE_OPTIONS} />
          </Form.Item>

          <Form.Item label="Status" name="status">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>

          <Form.Item label="All Day?" name="is_all_day" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item label="Event Dates" name="dates">
            <RangePicker showTime style={{ width: "100%" }} />
          </Form.Item>

          <Divider style={{ margin: "12px 0" }} />

          <Form.Item label="Estimated Cost (₩)" name="estimated_cost">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null
                  ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item label="Estimated Revenue (₩)" name="estimated_revenue">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null
                  ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item label="Actual Cost (₩)" name="actual_cost">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null
                  ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item label="Actual Revenue (₩)" name="actual_revenue">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null
                  ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Divider style={{ margin: "12px 0" }} />

          <Form.Item label="Ticket / Door Revenue (₩)" name="ticket_revenue">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null
                  ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item label="F&B Revenue (₩)" name="fnb_revenue">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null
                  ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item label="Marketing Cost (₩)" name="marketing_cost">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null
                  ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item label="Entertainment Cost (₩)" name="entertainment_cost">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null
                  ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Divider style={{ margin: "12px 0" }} />

          <Form.Item label="Misc Cost (₩)" name="misc_cost">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null
                  ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item label="Misc Cost Notes" name="misc_cost_notes">
            <TextArea rows={2} />
          </Form.Item>

          <Form.Item label="Misc Revenue (₩)" name="misc_revenue">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000000}
              formatter={(value) =>
                value != null
                  ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : ""
              }
              parser={(value) => (value ? value.replace(/,/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item label="Misc Revenue Notes" name="misc_revenue_notes">
            <TextArea rows={2} />
          </Form.Item>

          <Divider style={{ margin: "12px 0" }} />

          <Form.Item label="Primary Sponsor" name="primary_sponsor">
            <Input />
          </Form.Item>

          <Form.Item label="Internal Notes" name="notes">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EventDetailsPage;
